/**
 * Pure Playwright trace.zip screencast-frame extractor.
 *
 * Playwright records two independent visual streams when tracing with
 * `screenshots: true`:
 *
 *   1. Per-action DOM snapshots (before/after) — reconstructed into images by
 *      replaying them in a headless browser (`snapshot-reconstructor.ts`). These
 *      exist ONLY for actions that take a snapshot (click/fill/navigate/
 *      ariaSnapshot); a `page.evaluate()` read produces none.
 *   2. A continuous **screencast** — JPEG frames captured on a timer, emitted as
 *      `screencast-frame` events that reference a content-addressed resource.
 *      These exist at every visual moment, including `evaluate`/wait moments
 *      where there is no action snapshot.
 *
 * This module extracts (2). No browser, no Playwright runtime — just adm-zip +
 * JSON, mirroring `parseTraceActions`. The proven matching logic is lifted from
 * `scripts/scenarios/capture-from-trace.ts`.
 */

import AdmZip from 'adm-zip';

export interface ScreencastFrame {
  /**
   * Wall-clock epoch ms the frame was swapped onto the screen. This is the same
   * clock as `TraceAction.wallTime`, so frames and actions can be placed on one
   * absolute axis by the workspace writer.
   */
  frameSwapWallTimeMs: number;
  /** Page the frame belongs to. Popups/secondary pages have their own stream. */
  pageId: string;
  /** Content-addressed resource id referenced by the screencast-frame event. */
  sha1: string;
  /** JPEG bytes for the frame, resolved from the zip's `resources/` entries. */
  jpeg: Buffer;
}

export interface ExtractScreencastOptions {
  /**
   * Collapse runs of consecutive identical frames (same `sha1`, same `pageId`)
   * to the first occurrence — the instant the new pixels appeared. Screencast
   * repeats the same sha1 while the screen is idle, so this is a near-lossless
   * volume cut. Default `true`.
   */
  dedupeConsecutive?: boolean;
}

interface RawScreencastEvent {
  type?: string;
  pageId?: string;
  frameSwapWallTime?: number;
  sha1?: string;
}

/**
 * Build a sha1 → JPEG buffer index from the zip's `resources/` entries. Modern
 * Playwright stores screencast frames as `resources/<sha1>.jpeg`; older formats
 * use `resources/<sha1>`. Key by both the full basename and the
 * extension-stripped basename so either reference resolves.
 */
function buildResourceIndex(zip: AdmZip): Map<string, Buffer> {
  const index = new Map<string, Buffer>();
  for (const entry of zip.getEntries()) {
    if (entry.isDirectory || !entry.entryName.startsWith('resources/')) continue;
    const basename = entry.entryName.split('/').pop();
    if (!basename) continue;
    const data = entry.getData();
    index.set(basename, data);
    const dot = basename.indexOf('.');
    if (dot > 0) index.set(basename.slice(0, dot), data);
  }
  return index;
}

/**
 * Extract the screencast filmstrip from a Playwright trace.zip, sorted by
 * frame-swap wall time. Frames whose resource is missing from the archive are
 * dropped. Returns an empty array when the trace has no screencast events (older
 * traces, or tracing recorded with `screenshots: false`).
 */
export function extractScreencastFrames(
  traceZipPath: string,
  options: ExtractScreencastOptions = {},
): ScreencastFrame[] {
  const dedupeConsecutive = options.dedupeConsecutive ?? true;

  const zip = new AdmZip(traceZipPath);
  // Same filter as parseTraceActions: every `.trace` segment (a stitched
  // multi-segment zip has more than one), never the `.network` sidecar.
  const traceEntries = zip
    .getEntries()
    .filter((e) => e.entryName.endsWith('.trace') && !e.entryName.endsWith('.network'));

  if (traceEntries.length === 0) {
    throw new Error('No .trace entries found in the zip archive');
  }

  const resourceIndex = buildResourceIndex(zip);

  const frames: ScreencastFrame[] = [];
  for (const entry of traceEntries) {
    const content = entry.getData().toString('utf-8');
    for (const line of content.split('\n')) {
      if (!line.trim()) continue;
      let event: RawScreencastEvent;
      try {
        event = JSON.parse(line);
      } catch {
        continue;
      }
      if (event.type !== 'screencast-frame') continue;
      if (!event.sha1 || typeof event.frameSwapWallTime !== 'number') continue;
      const jpeg = resourceIndex.get(event.sha1);
      if (!jpeg) continue; // resource pruned / corrupt — skip rather than emit a broken frame
      frames.push({
        frameSwapWallTimeMs: event.frameSwapWallTime,
        pageId: event.pageId ?? '',
        sha1: event.sha1,
        jpeg,
      });
    }
  }

  // Segment-stable ordering: wall time is monotonic across stitched segments,
  // unlike the per-segment `startTime` clock.
  frames.sort((a, b) => a.frameSwapWallTimeMs - b.frameSwapWallTimeMs);

  if (!dedupeConsecutive) return frames;

  const deduped: ScreencastFrame[] = [];
  for (const frame of frames) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.sha1 === frame.sha1 && prev.pageId === frame.pageId) continue;
    deduped.push(frame);
  }
  return deduped;
}
